import React, { useState } from 'react';
import { IonPage } from '@ionic/react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  IconButton
} from '@mui/material';
import { 
  AccessTime, 
  CheckCircle, 
  History, 
  Schedule, 
  Today, 
  Refresh 
} from '@mui/icons-material';

const MarkAttendance: React.FC = () => {
  // Sample attendance data
  const [attendanceRecords, setAttendanceRecords] = useState([
    { id: 1, date: '2023-10-15', time: '09:05 AM', status: 'Present', notes: 'Slightly late due to traffic' },
    { id: 2, date: '2023-10-14', time: '08:55 AM', status: 'Present', notes: '' },
    { id: 3, date: '2023-10-13', time: '09:15 AM', status: 'Late', notes: 'Public transport delay' },
    { id: 4, date: '2023-10-12', time: '08:45 AM', status: 'Present', notes: '' },
    { id: 5, date: '2023-10-11', time: '09:00 AM', status: 'Present', notes: '' },
  ]);

  const [currentStatus, setCurrentStatus] = useState('Not marked yet');
  const [isLoading, setIsLoading] = useState(false);

  // Function to mark attendance
  const markAttendance = () => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const hours = now.getHours();
      const minutes = now.getMinutes();
      
      let status = 'Present';
      let notes = '';
      
      // Determine status based on time (assuming 9 AM as the cutoff for being late)
      if (hours > 9 || (hours === 9 && minutes > 0)) {
        status = 'Late';
        notes = `Arrived at ${time}`;
      }
      
      // Create new record
      const newRecord = {
        id: attendanceRecords.length + 1,
        date,
        time,
        status,
        notes
      };
      
      // Update state
      setAttendanceRecords([newRecord, ...attendanceRecords]);
      setCurrentStatus(status);
      setIsLoading(false);
    }, 1500);
  };

  // Function to get status chip color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Present': return 'success';
      case 'Late': return 'warning';
      case 'Absent': return 'error';
      default: return 'default';
    }
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <IonPage className='pushDown'>
      <Box sx={{ p: 2, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AccessTime sx={{ fontSize: 32, mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            Employee Attendance
          </Typography>
          <IconButton sx={{ ml: 'auto' }} onClick={() => window.location.reload()}>
            <Refresh />
          </IconButton>
        </Box>

        {/* Current Status Card */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Today color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="h2">
                Today's Status
              </Typography>
            </Box>
            
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Marking attendance...
                </Typography>
              </Box>
            ) : (
              <>
                <Chip 
                  label={currentStatus} 
                  color={getStatusColor(currentStatus)} 
                  sx={{ fontSize: '1rem', p: 2, mb: 2 }}
                  icon={currentStatus !== 'Not marked yet' ? <CheckCircle /> : undefined}
                />
                
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={markAttendance}
                  disabled={currentStatus !== 'Not marked yet'}
                  startIcon={<Schedule />}
                  sx={{ py: 1.5, borderRadius: 2 }}
                >
                  Mark Today's Attendance
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Attendance History */}
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <History color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="h2">
                Attendance History
              </Typography>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{formatDate(record.date)}</TableCell>
                      <TableCell>{record.time}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.status} 
                          color={getStatusColor(record.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{record.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </IonPage>
  );
};

export default MarkAttendance;